import React, { FormEvent } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { Button } from '../buttons/Button';
import { CheckoutFormProps } from '@usacsmideo/types';
import * as Sentry from '@sentry/browser';

/** How long the PaymentElement iframe may take to report ready before the patient is offered a reload. */
export const PAYMENT_ELEMENT_READY_TIMEOUT_MS = 20_000;

export const CheckoutForm = (props: CheckoutFormProps) => {
  const stripe = useStripe();
  const elements = useElements();

  const [errorMessage, setErrorMessage] = React.useState('');
  // The PaymentElement loads in its own iframe after Stripe.js is up. Until it
  // reports ready there is only blank space, and if it fails to load there was
  // previously nothing at all (CU-17tm466h6uv).
  const [elementReady, setElementReady] = React.useState(false);
  const [elementLoadFailed, setElementLoadFailed] = React.useState(false);
  // An iframe that never reports ready OR an error would otherwise leave
  // "Loading payment form..." up forever. After the timeout the reload panel
  // shows; if the element does become ready later, the form is usable again.
  const [elementTimedOut, setElementTimedOut] = React.useState(false);

  React.useEffect(() => {
    if (elementReady || elementLoadFailed) return;
    const timer = setTimeout(() => {
      setElementTimedOut(true);
      Sentry.captureException(
        new Error(`PaymentElement not ready after ${PAYMENT_ELEMENT_READY_TIMEOUT_MS}ms`),
        { fingerprint: ['payment-element', 'ready-timeout'], tags: { payment_element: 'ready-timeout' } },
      );
    }, PAYMENT_ELEMENT_READY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [elementReady, elementLoadFailed]);

  const showElementError = elementLoadFailed || (elementTimedOut && !elementReady);

  const handlePaymentElementLoadError = (event: { error: { message?: string; type?: string } }) => {
    setElementLoadFailed(true);
    Sentry.captureException(new Error(`PaymentElement failed to load: ${event.error?.type ?? 'unknown'}`), {
      fingerprint: ['payment-element', 'load-error'],
      tags: { payment_element: 'load-error' },
      extra: { detail: event.error?.message },
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault();

    if (!stripe || !elements || !elementReady) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    const { error } = await stripe.confirmPayment({
      //`Elements` instance that was used to create the Payment Element
      elements,
      confirmParams: {
        //TODO: UPDATE THIS URL TO REDIRECT TO MEDICARE/INSURANCE IF NECESSARY
        // NO PAYMENT DETAILS IN THIS URL (CU-17tm466h63h). Stripe appends `payment_intent`, and the
        // thanks page asks the server to record it. Amounts here were read back as truth, so typing
        // the URL marked a patient paid.
        return_url: `${import.meta.env.VITE_PORTAL_BASE_URL}/${props.patientId}/payment/thanks`,
      },
    });

    if (error) {
      Sentry.captureException(error);
      // This point will only be reached if there is an immediate error when confirming the payment. Show error to your customer (for example, payment details incomplete)
      if (error.message) {
        setErrorMessage(error.message);
      }
    }
    // Otherwise Stripe redirects to `return_url`. This form never records the payment itself: the
    // server does, from Stripe's own answer, when the thanks page asks (CU-17tm466h63h).
  };

  return (
    <form onSubmit={handleSubmit}>
      {!elementReady && !showElementError && (
        <p id="payment-element-loading" className="py-4 text-center text-lg text-gray-600">
          Loading payment form...
        </p>
      )}
      {showElementError && (
        <div id="payment-element-error" className="my-4 rounded-md border border-red-300 bg-red-50 p-6 text-center">
          <p className="text-lg font-semibold text-red-800">The payment form could not be loaded.</p>
          <p className="mt-2 text-sm text-red-600">Reloading the page usually fixes this.</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 rounded-md bg-sky-700 px-6 py-2 text-white hover:bg-sky-800"
          >
            Reload Page
          </button>
          <p className="mt-3 text-xs text-gray-500">
            If this issue persists, please contact{' '}
            <a href="mailto:info@mideohealth.com" className="underline">
              info@mideohealth.com
            </a>
          </p>
        </div>
      )}
      <PaymentElement onReady={() => setElementReady(true)} onLoadError={handlePaymentElementLoadError} />
      <Button
        id="submit-payment"
        type="submit"
        textContent="Submit Payment"
        disabled={!stripe || !elementReady}
        onClick={handleSubmit}
      />
      {/* Show error message to your customers */}
      {errorMessage && <div>{errorMessage}</div>}
    </form>
  );
};
